import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getApiErrorMessage } from '../utils/apiError';

export const SignInModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onRequestInvite?: () => void;
}> = ({ isOpen, onClose, onRequestInvite }) => {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return !!email.trim() && !!password && !loading;
  }, [email, password, loading]);

  if (!isOpen) return null;

  const reset = () => {
    setEmail('');
    setPassword('');
    setLoading(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
      handleClose();
      navigate('/dashboard');
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Failed to sign in');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const openRequestInvite = () => {
    handleClose();
    onRequestInvite?.();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-purple-200 overflow-hidden">
        <div className="p-6 bg-gradient-to-br from-purple-600 via-purple-500 to-purple-400 flex items-center justify-between">
          <div>
            <div className="text-2xl font-black text-white">Sign in</div>
            <div className="text-purple-100 text-sm">Beta access</div>
          </div>
          <button onClick={handleClose} className="text-white/90 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 bg-white">
          {error && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-700 text-sm mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold mb-2 text-slate-700">Email</label>
              <input
                className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                type="email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 text-slate-700">Password</label>
              <input
                className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                required
              />
              <div className="mt-1 text-right">
                <Link
                  to="/forgot-password"
                  onClick={handleClose}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-slate-600">
            Doesn’t have an account?{' '}
            <button
              type="button"
              onClick={openRequestInvite}
              className="text-purple-600 hover:text-purple-700 font-bold underline underline-offset-2"
            >
              Request invite
            </button>
          </div>

          <div className="mt-3 text-center text-xs text-slate-500">
            Prefer the full page?{' '}
            <a className="text-purple-600 hover:text-purple-700 underline underline-offset-2" href="/login">
              Open sign in page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

